/**
 * Labstep
 *
 * @module containers/Entity/Read/Entity
 * @desc Container for reading one entity
 */

import type {
  APICallOptions,
  APICallStatus,
  EntityClassForName,
} from 'labstep-web/models';
import type { EntityName } from 'labstep-web/services/schema/schema';
import { readEntity } from 'labstep-web/state/actions/entity';
import {
  selectEntity,
  selectReadEntityStatus,
} from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { IEntityReadEntityContainerProps } from './types';

export type EntityReadEntityContainerChildrenProps<
  T extends EntityName,
> = {
  entity: EntityClassForName<T> | null;
  status: APICallStatus | null;
  read: (options?: APICallOptions) => void;
};

export const EntityReadEntityContainer = <T extends EntityName>({
  children,
  entityName,
  ...props
}: IEntityReadEntityContainerProps<T>): React.ReactElement | null => {
  const dispatch = useDispatch();

  // Determine the ID from props (could be id or guid)
  let id: string | number | undefined;
  if ('guid' in props) {
    const entities = useSelector((state: any) =>
      state.entities
        ? Object.values(state.entities[entityName].byId)
        : [],
    );
    id = (entities as any[]).find(
      (entity: any) => entity.guid === props.guid,
    )?.id;
  } else {
    id = props.id;
  }

  // Use selectors to get state
  const entity = useSelector((state) =>
    id ? selectEntity(state, entityName, id) : null,
  );
  const status = useSelector((state) =>
    id ? selectReadEntityStatus(state, entityName, id) : null,
  );

  // Memoized read function
  const read = useCallback(
    (options?: APICallOptions) => {
      const readId = 'guid' in props ? props.guid : props.id;
      dispatch(readEntity(entityName, readId, options));
    },
    [dispatch, entityName, props],
  );

  return children({
    entity,
    status,
    read,
  });
};
